import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FaTimes,
  FaBuilding,
  FaPaperPlane,
  FaChevronLeft,
  FaExclamationCircle,
} from 'react-icons/fa';

import { db } from '../../firebase';
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';

import styles from '../CSS/DepartamentosModal.module.css';

/**
 * Componente modal para crear o editar un departamento.
 */
const DepartamentoFormModal = ({
  user,
  modo, // 'crear' o 'editar'
  departamento = null, // Solo requerido en modo editar
  onClose,
  onUpdateDepartamentos
}) => {
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: departamento?.nombre || '',
    descripcion: departamento?.descripcion || ''
  });
  
  // Estado de errores
  const [errors, setErrors] = useState({});
  
  // Estado de carga cuando se está enviando el formulario
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Referencias para los campos del formulario
  const nombreInputRef = useRef(null);
  
  // Establecer el foco en el primer campo al montar el componente
  useEffect(() => {
    if (nombreInputRef.current) {
      nombreInputRef.current.focus();
    }
  }, []);
  
  // Manejador de cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Limpiar error del campo si existe
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };
  
  // Validación del formulario
  const validateForm = () => {
    const newErrors = {};
    
    // Validación del nombre
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es obligatorio';
    } else if (formData.nombre.trim().length > 50) {
      newErrors.nombre = 'El nombre no puede exceder 50 caracteres';
    }
    
    // Validación de la descripción
    if (formData.descripcion.trim().length > 300) {
      newErrors.descripcion = 'La descripción no puede exceder 300 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      if (modo === 'crear') {
        // Crear departamento
        const docRef = await addDoc(collection(db, 'departamentos'), {
          ...formData,
          created_at: Timestamp.now(),
          created_by: user.uid
        });
        
        onUpdateDepartamentos(prev => [
          ...prev,
          {
            id: docRef.id,
            ...formData,
            created_at: new Date(),
            created_by: user.uid
          }
        ]);
      } else if (modo === 'editar' && departamento) {
        // Editar departamento existente
        const departamentoRef = doc(db, 'departamentos', departamento.id);
        
        await updateDoc(departamentoRef, {
          ...formData,
          updated_at: Timestamp.now(),
          updated_by: user.uid
        });
        
        onUpdateDepartamentos(prev =>
          prev.map(dep =>
            dep.id === departamento.id
              ? {
                  ...dep,
                  ...formData,
                  updated_at: new Date(),
                  updated_by: user.uid
                }
              : dep
          )
        );
      }
      
      // Cerrar el modal después de completar
      onClose();
    } catch (error) {
      console.error('Error:', error);
      setErrors({ submit: 'Ha ocurrido un error. Inténtalo de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Animaciones para el modal
  const modalVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };
  
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } }
  };
  
  return (
    <motion.div
      className={styles.modalOverlay}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={overlayVariants}
    >
      <motion.div
        className={styles.modalContent}
        variants={modalVariants}
        style={{ maxWidth: '700px' }}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaBuilding className={styles.titleIcon} />
            {modo === 'crear' ? 'Crear Nuevo Departamento' : 'Editar Departamento'}
          </h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.formContainer}>
            <div className={styles.formHeader}>
              <button
                className={`${styles.btn} ${styles.btnSecondary} ${styles.btnBack}`}
                onClick={onClose}
                type="button"
              >
                <FaChevronLeft />
                <span>Volver</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.departamentoForm}>
              {errors.submit && (
                <div className={styles.errorMessage}>
                  <FaExclamationCircle className={styles.errorIcon} />
                  {errors.submit}
                </div>
              )}

             <div className={styles.formGroup}>
                <label htmlFor="nombre" className={styles.formLabel}>
                  Nombre del departamento*
                  <span className={styles.characterCounter}>
                    {formData.nombre.length}/50
                  </span>
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  ref={nombreInputRef}
                  className={`${styles.formControl} ${errors.nombre ? styles.formControlError : ''}`}
                  value={formData.nombre}
                  onChange={handleChange}
                  placeholder="Ej. Soporte Técnico"
                  disabled={isSubmitting}
                  maxLength={50}
                  autoFocus
                />
                {errors.nombre && (
                  <div className={styles.fieldError}>
                    <FaExclamationCircle className={styles.errorIcon} />
                    {errors.nombre}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="descripcion" className={styles.formLabel}>
                  Descripción (opcional)
                  <span className={styles.characterCounter}>
                    {formData.descripcion.length}/300
                  </span>
                </label>
                <textarea
                  id="descripcion"
                  name="descripcion"
                  className={`${styles.formControl} ${errors.descripcion ? styles.formControlError : ''}`}
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Describe la función de este departamento"
                  rows={5}
                  disabled={isSubmitting}
                  maxLength={300}
                />
                {errors.descripcion && (
                  <div className={styles.fieldError}>
                    <FaExclamationCircle className={styles.errorIcon} />
                    {errors.descripcion}
                  </div>
                )}
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  <FaTimes />
                  <span>Cancelar</span>
                </button>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className={styles.spinner}></span>
                      <span>
                        {modo === 'crear' ? 'Creando...' : 'Actualizando...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      <span>
                        {modo === 'crear'
                          ? 'Crear Departamento'
                          : 'Actualizar Departamento'}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DepartamentoFormModal;